import React from 'react';
import { PRIORITY_CONFIG, CATEGORY_CONFIG, STATUS_CONFIG } from './taskConfigs';
import {
  CalendarDays, User, MessageSquare, ArrowRight, MoreVertical,
  Edit3, Check, Trash2, GripVertical, Repeat, Zap, Bell
} from 'lucide-react';

const TEAM_TASKS_PER_PAGE = 5;

export const TaskCard = ({ task, compact = false, serialNum, mainTab, setDraggedTask, handleQuickStatus, activeMenu, setActiveMenu, setEditingTask, setShowCreateModal, setShowCompleteModal, handleDelete }) => {
  const pri = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const cat = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.custom;
  const stat = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const StatusIcon = stat.icon;
  const isOverdue = task.due_date && task.due_date < new Date().toISOString().split('T')[0] && !['completed', 'not_completed'].includes(task.status);
  const isAutoCreated = (task.tags || []).includes('auto-created');
  const hasReminder = task.reminders && task.reminders.length > 0;

  return (
    <div
      className={`bg-white rounded-xl border ${isOverdue ? 'border-red-300 bg-red-50/30' : 'border-gray-200'} ${compact ? 'p-2' : 'p-3'} hover:shadow-md transition-all group relative`}
      data-testid={`task-card-${task.id}`}
      draggable={mainTab === 'kanban'}
      onDragStart={() => setDraggedTask(task)}
      onDragEnd={() => setDraggedTask(null)}
    >
      <div className="flex items-start gap-2.5">
        {serialNum != null && (
          <span className="text-xs font-bold text-gray-400 mt-1 w-5 text-right shrink-0" data-testid={`task-serial-${task.id}`}>{serialNum}.</span>
        )}
        {mainTab === 'kanban' && <GripVertical className="w-4 h-4 text-gray-300 mt-0.5 shrink-0 cursor-grab" />}
        <button onClick={() => handleQuickStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')} className="mt-0.5 shrink-0" data-testid={`task-toggle-${task.id}`}>
          <StatusIcon className={`w-5 h-5 ${stat.color} hover:scale-110 transition-transform`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`text-base font-semibold ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{task.title}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${pri.bg} ${pri.text} ${pri.border} border`}>{pri.label}</span>
            {task.recurrence && task.recurrence !== 'none' && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200 flex items-center gap-0.5">
                <Repeat className="w-3 h-3" /> {task.recurrence}
              </span>
            )}
            {isAutoCreated && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200 flex items-center gap-0.5">
                <Zap className="w-3 h-3" /> Auto
              </span>
            )}
            {hasReminder && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 flex items-center gap-0.5" title={`Reminder: ${task.reminders[0].label || task.reminders[0].type}`}>
                <Bell className="w-3 h-3" />
              </span>
            )}
          </div>
          {!compact && task.description && <p className="text-sm text-gray-500 mb-1 line-clamp-1">{task.description}</p>}
          <div className="flex items-center gap-2.5 text-xs text-gray-400 flex-wrap">
            <span className={cat.color}>{cat.icon} {cat.label}</span>
            {task.due_date && <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : ''}`}><CalendarDays className="w-3.5 h-3.5" />{task.due_date}{task.due_time ? ` ${task.due_time}` : ''}</span>}
            {task.assigned_to?.name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{task.assigned_to.name}</span>}
            {task.linked_lead_name && <span className="flex items-center gap-1 text-teal-500"><ArrowRight className="w-3.5 h-3.5" />{task.linked_lead_name}</span>}
            {!compact && task.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{task.comments.length}</span>}
            {!compact && task.completion_remarks && <span className="italic text-gray-400">"{task.completion_remarks}"</span>}
          </div>
        </div>
        <div className="relative shrink-0">
          <button onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === task.id ? null : task.id); }} className="p-1 rounded-lg hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`task-menu-${task.id}`}>
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
          {activeMenu === task.id && (
            <div className="fixed z-50 bg-white border rounded-xl shadow-lg py-1 w-40" style={{ top: 'auto', right: 'auto' }} ref={el => {
              if (el) {
                const btn = el.parentElement.querySelector('button');
                if (btn) {
                  const rect = btn.getBoundingClientRect();
                  el.style.top = `${rect.bottom + 4}px`;
                  el.style.left = `${rect.right - 160}px`;
                }
              }
            }} onMouseLeave={() => setActiveMenu(null)}>
              <button onClick={() => { setEditingTask(task); setShowCreateModal(true); setActiveMenu(null); }} className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid={`task-edit-${task.id}`}><Edit3 className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => { setShowCompleteModal(task); setActiveMenu(null); }} className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"><Check className="w-3.5 h-3.5" /> Complete</button>
              <button onClick={() => { handleDelete(task.id); setActiveMenu(null); }} className="w-full px-3 py-1.5 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid={`task-delete-${task.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { TEAM_TASKS_PER_PAGE };
